import React, { useState, useEffect } from 'react';
import {
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import {
  DollarSign,
  TrendingUp,
  Users,
  Activity,
  Zap,
  Database,
  CheckCircle,
  Target,
  Calendar,
  Download,
} from 'lucide-react';
import { BRAND_COLORS, CHART_COLOR_ARRAY } from '../constants/colors';
import { buildCostBreakdownSlices, sliceLabel } from '../utils/costBreakdownSlices';
import type { DetailedLLMAnalytics } from '../types/unified-analytics';

const API_URL = import.meta.env.VITE_API_URL || '';

// KPI Card Component
interface KPICardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  trend?: string;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, subtitle, icon, color, trend }) => {
  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-2">{title}</p>
          <p className="text-3xl font-mono font-bold text-gray-900 mb-2">{value}</p>
          {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          {trend && (
            <div className="flex items-center mt-2 text-sm font-medium text-green-600">
              <TrendingUp className="w-4 h-4 mr-1" />
              {trend}
            </div>
          )}
        </div>
        <div className="p-3 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <div style={{ color }}>{icon}</div>
        </div>
      </div>
    </div>
  );
};

// Mini Metric Card
interface MiniMetricCardProps {
  title: string;
  value: string;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
}

const MiniMetricCard: React.FC<MiniMetricCardProps> = ({ title, value, subtitle, icon, color }) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}20` }}>
          <div style={{ color }} className="w-4 h-4">
            {icon}
          </div>
        </div>
        <span className="text-xs font-medium text-gray-600">{title}</span>
      </div>
      <p className="text-2xl font-mono font-bold mb-1" style={{ color }}>
        {value}
      </p>
      <p className="text-xs text-gray-500">{subtitle}</p>
    </div>
  );
};

const UnifiedAnalyticsDashboard: React.FC = () => {
  const [data, setData] = useState<DetailedLLMAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [granularity, setGranularity] = useState('day');

  useEffect(() => {
    fetchData();
  }, [timeRange, granularity]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Calculate date range based on selection
      const now = new Date();
      let startDate = new Date();

      switch (timeRange) {
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case '1y':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }

      const params = new URLSearchParams({
        start_date: startDate.toISOString().split('T')[0],
        end_date: now.toISOString().split('T')[0],
        granularity: granularity,
      });

      const response = await fetch(`${API_URL}/admin/analytics/llm-usage?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch analytics data');
      }

      const analyticsData = await response.json();
      setData(analyticsData);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load analytics data');
      console.error('Error fetching analytics data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div
          className="animate-spin rounded-full h-12 w-12 border-b-2"
          style={{ borderColor: BRAND_COLORS.primary }}
        ></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
        <p className="font-medium">Error loading analytics data</p>
        <p className="text-sm mt-1">{error || 'Unknown error'}</p>
        <button
          onClick={fetchData}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  const {
    summary,
    cost_breakdown,
    by_provider,
    by_usecase,
    over_time,
    efficiency_metrics,
    cost_distribution,
  } = data;

  // Use calculated total
  const totalCost = cost_breakdown.total_calculated;
  const totalSimulations = efficiency_metrics.total_simulations || 1;

  const costBreakdownData = buildCostBreakdownSlices(cost_breakdown);

  // Calculate multimodal percentages
  const totalMultimodalCost = cost_breakdown.image_cost + cost_breakdown.video_cost;
  const multimodalPercentage = totalCost > 0 ? (totalMultimodalCost / totalCost) * 100 : 0;

  // Handle CSV export
  const handleExport = async () => {
    try {
      const now = new Date();
      let startDate = new Date();

      switch (timeRange) {
        case '24h':
          startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          break;
        case '7d':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case '30d':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case '90d':
          startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
          break;
        case '1y':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      }

      const params = new URLSearchParams({
        start_date: startDate.toISOString().split('T')[0],
        end_date: now.toISOString().split('T')[0],
      });

      const response = await fetch(`${API_URL}/admin/analytics/llm-usage/export?${params}`, {
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `llm-usage-${timeRange}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Header with Time Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
            <p className="text-sm text-gray-500">
              Real-time insights into platform performance and AI costs
            </p>
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>

        {/* Time Range Selector */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5" style={{ color: BRAND_COLORS.primary }} />
            <span className="text-sm font-semibold text-gray-700">Time Range</span>
          </div>
          <div className="flex gap-2">
            {[
              { value: '24h', label: '24 Hours', gran: 'hour' },
              { value: '7d', label: '7 Days', gran: 'day' },
              { value: '30d', label: '30 Days', gran: 'day' },
              { value: '90d', label: '90 Days', gran: 'week' },
              { value: '1y', label: '1 Year', gran: 'month' },
            ].map((range) => (
              <button
                key={range.value}
                onClick={() => {
                  setTimeRange(range.value);
                  setGranularity(range.gran);
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  timeRange === range.value
                    ? 'text-white shadow-sm'
                    : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                }`}
                style={timeRange === range.value ? { backgroundColor: BRAND_COLORS.primary } : {}}
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* SECTION 1: Hero Metrics (5 KPI Cards) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          title="Total AI Cost"
          value={`$${totalCost.toFixed(2)}`}
          subtitle={`$${(totalCost / summary.total_requests).toFixed(4)} per request`}
          icon={<DollarSign className="w-6 h-6" />}
          color={BRAND_COLORS.primary}
        />
        <KPICard
          title="Total Requests"
          value={summary.total_requests.toLocaleString()}
          subtitle={`${summary.success_rate.toFixed(1)}% success rate`}
          icon={<Activity className="w-6 h-6" />}
          color={BRAND_COLORS.success}
        />
        <KPICard
          title="Total Tokens"
          value={`${(summary.total_total_tokens / 1000).toFixed(1)}K`}
          subtitle={`${summary.total_total_tokens.toLocaleString()} tokens`}
          icon={<Database className="w-6 h-6" />}
          color={BRAND_COLORS.purple}
        />
        <KPICard
          title="Avg Response"
          value={`${(summary.avg_response_time_ms / 1000).toFixed(1)}s`}
          subtitle={
            summary.failed_requests > 0 ? `${summary.failed_requests} failed` : 'All successful'
          }
          icon={<Zap className="w-6 h-6" />}
          color={BRAND_COLORS.warning}
        />
        <KPICard
          title="Cost per Sim"
          value={`$${efficiency_metrics.avg_cost_per_simulation.toFixed(3)}`}
          subtitle={`${totalSimulations} simulations`}
          icon={<Target className="w-6 h-6" />}
          color={BRAND_COLORS.teal}
        />
      </div>

      {/* SECTION 2: Cost Intelligence */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-6">
          <DollarSign className="w-5 h-5" style={{ color: BRAND_COLORS.primary }} />
          <h2 className="text-xl font-bold text-gray-900">Cost Intelligence</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cost Breakdown Donut Chart */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Cost Breakdown</h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={costBreakdownData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={sliceLabel}
                  outerRadius={70}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {costBreakdownData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => `$${value.toFixed(4)}`}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
            <ul className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
              {costBreakdownData.map((slice) => (
                <li key={slice.name} className="flex items-center gap-1.5">
                  <span
                    className="inline-block h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: slice.color }}
                  />
                  <span className="truncate">{slice.name}</span>
                  <span className="ml-auto tabular-nums">${slice.value.toFixed(2)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 text-center">
              <p className="text-sm font-semibold text-gray-700">Total: ${totalCost.toFixed(4)}</p>
            </div>
          </div>

          {/* Cost & Usage Trend */}
          <div className="lg:col-span-2 bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Cost & Usage Trend (
              {timeRange === '24h'
                ? '24 Hours'
                : timeRange === '7d'
                  ? '7 Days'
                  : timeRange === '30d'
                    ? '30 Days'
                    : timeRange === '90d'
                      ? '90 Days'
                      : '1 Year'}
              )
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={over_time}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke={BRAND_COLORS.gray400} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke={BRAND_COLORS.gray400} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11 }}
                  stroke={BRAND_COLORS.gray400}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
                <Bar
                  yAxisId="right"
                  dataKey="count"
                  name="Requests"
                  fill={BRAND_COLORS.primary}
                  opacity={0.6}
                />
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="cost"
                  name="Cost ($)"
                  stroke={BRAND_COLORS.danger}
                  strokeWidth={2}
                  dot={{ fill: BRAND_COLORS.danger, r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Provider Performance Matrix - Multi-Chart View */}
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Provider Performance Comparison
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Cost Comparison */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-xs font-medium text-gray-600 mb-3">Total Cost</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={by_provider} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="provider" width={70} tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => `$${value.toFixed(2)}`}
                  />
                  <Bar dataKey="cost" fill={BRAND_COLORS.danger} radius={[0, 4, 4, 0]}>
                    {by_provider.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLOR_ARRAY[index % CHART_COLOR_ARRAY.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Request Volume */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-xs font-medium text-gray-600 mb-3">Request Volume</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={by_provider} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="provider" width={70} tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => value.toLocaleString()}
                  />
                  <Bar dataKey="count" fill={BRAND_COLORS.primary} radius={[0, 4, 4, 0]}>
                    {by_provider.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLOR_ARRAY[index % CHART_COLOR_ARRAY.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Response Time */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-xs font-medium text-gray-600 mb-3">Avg Response Time</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  data={by_provider.map((p) => ({
                    ...p,
                    response_time_seconds: p.avg_response_time_ms / 1000,
                  }))}
                  layout="vertical"
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis type="category" dataKey="provider" width={70} tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                    formatter={(value: number) => `${value.toFixed(1)}s`}
                  />
                  <Bar
                    dataKey="response_time_seconds"
                    fill={BRAND_COLORS.warning}
                    radius={[0, 4, 4, 0]}
                  >
                    {by_provider.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLOR_ARRAY[index % CHART_COLOR_ARRAY.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Provider Stats Table */}
          <div className="mt-4 bg-gray-50 rounded-lg p-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 text-xs font-semibold text-gray-600">
                      Provider
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      Total Cost
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      Requests
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      Avg Response
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      Success Rate
                    </th>
                    <th className="text-right py-2 px-3 text-xs font-semibold text-gray-600">
                      Cost/1K Tokens
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {by_provider.map((provider, idx) => (
                    <tr key={provider.provider} className="border-b border-gray-100 hover:bg-white">
                      <td className="py-2 px-3 text-sm">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor: CHART_COLOR_ARRAY[idx % CHART_COLOR_ARRAY.length],
                            }}
                          />
                          <span className="font-semibold text-gray-900 capitalize">
                            {provider.provider}
                          </span>
                        </div>
                      </td>
                      <td
                        className="py-2 px-3 text-sm text-right font-mono font-bold"
                        style={{ color: BRAND_COLORS.danger }}
                      >
                        ${provider.cost.toFixed(2)}
                      </td>
                      <td className="py-2 px-3 text-sm text-right font-mono">
                        {provider.count.toLocaleString()}
                      </td>
                      <td className="py-2 px-3 text-sm text-right font-mono">
                        {(provider.avg_response_time_ms / 1000).toFixed(1)}s
                      </td>
                      <td
                        className="py-2 px-3 text-sm text-right font-semibold"
                        style={{
                          color:
                            provider.success_rate >= 95
                              ? BRAND_COLORS.success
                              : BRAND_COLORS.warning,
                        }}
                      >
                        {provider.success_rate.toFixed(0)}%
                      </td>
                      <td className="py-2 px-3 text-sm text-right font-mono text-gray-600">
                        ${provider.cost_per_1k_tokens.toFixed(4)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* SECTION 2.5: Multimodal Usage Analytics */}
      {(summary.total_num_images > 0 || summary.total_num_videos > 0) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-2 mb-6">
            <Database className="w-5 h-5" style={{ color: '#10B981' }} />
            <h2 className="text-xl font-bold text-gray-900">Multimodal Usage</h2>
            <span className="text-sm text-gray-500 ml-auto">
              {multimodalPercentage.toFixed(1)}% of total cost
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <KPICard
              title="Images Processed"
              value={summary.total_num_images.toLocaleString()}
              subtitle={`${summary.total_image_tokens.toLocaleString()} tokens`}
              icon={<Database className="w-5 h-5" />}
              color="#10B981"
            />
            <KPICard
              title="Videos Processed"
              value={summary.total_num_videos.toLocaleString()}
              subtitle={`${summary.total_video_tokens.toLocaleString()} tokens`}
              icon={<Activity className="w-5 h-5" />}
              color="#06B6D4"
            />
            <KPICard
              title="Image Cost"
              value={`$${cost_breakdown.image_cost.toFixed(2)}`}
              subtitle={`${((cost_breakdown.image_cost / totalCost) * 100).toFixed(1)}% of total`}
              icon={<DollarSign className="w-5 h-5" />}
              color="#10B981"
            />
            <KPICard
              title="Video Cost"
              value={`$${cost_breakdown.video_cost.toFixed(2)}`}
              subtitle={`${((cost_breakdown.video_cost / totalCost) * 100).toFixed(1)}% of total`}
              icon={<DollarSign className="w-5 h-5" />}
              color="#06B6D4"
            />
          </div>

          {/* Multimodal Usage by Use Case - Stacked Bar Chart */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Multimodal Usage by Use Case
            </h3>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={by_usecase
                  .filter((uc) => uc.num_images > 0 || uc.num_videos > 0)
                  .sort((a, b) => b.num_images + b.num_videos - (a.num_images + a.num_videos))
                  .slice(0, 10)}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  type="number"
                  tick={{ fontSize: 11 }}
                  stroke={BRAND_COLORS.gray400}
                  label={{
                    value: 'Count',
                    position: 'insideBottom',
                    offset: -5,
                    style: { fontSize: 11 },
                  }}
                />
                <YAxis
                  type="category"
                  dataKey="usecase"
                  width={150}
                  tick={{ fontSize: 10 }}
                  stroke={BRAND_COLORS.gray400}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                  }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      const totalMultimedia = data.num_images + data.num_videos;
                      return (
                        <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                          <p className="text-sm font-semibold text-gray-900 mb-2">{data.usecase}</p>
                          <div className="space-y-1">
                            <p className="text-xs text-gray-600 flex items-center gap-2">
                              <span
                                className="inline-block w-2 h-2 rounded-full"
                                style={{ backgroundColor: '#10B981' }}
                              />
                              Images:{' '}
                              <span className="font-mono font-bold" style={{ color: '#10B981' }}>
                                {data.num_images}
                              </span>
                              <span className="text-gray-400">
                                ({data.image_tokens.toLocaleString()} tokens)
                              </span>
                            </p>
                            <p className="text-xs text-gray-600 flex items-center gap-2">
                              <span
                                className="inline-block w-2 h-2 rounded-full"
                                style={{ backgroundColor: '#06B6D4' }}
                              />
                              Videos:{' '}
                              <span className="font-mono font-bold" style={{ color: '#06B6D4' }}>
                                {data.num_videos}
                              </span>
                              <span className="text-gray-400">
                                ({data.video_tokens.toLocaleString()} tokens)
                              </span>
                            </p>
                            <p className="text-xs text-gray-600 pt-1 border-t border-gray-200">
                              Total: <span className="font-mono font-bold">{totalMultimedia}</span>
                            </p>
                          </div>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend />
                <Bar
                  dataKey="num_images"
                  name="Images"
                  stackId="a"
                  fill="#10B981"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="num_videos"
                  name="Videos"
                  stackId="a"
                  fill="#06B6D4"
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* SECTION 3: Top Users by Cost */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5" style={{ color: BRAND_COLORS.primary }} />
          <h2 className="text-xl font-bold text-gray-900">Top Users by AI Cost</h2>
          <span className="text-xs text-gray-500 ml-2">(Top 20)</span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={cost_distribution} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke={BRAND_COLORS.gray200} />
            <XAxis
              type="number"
              tick={{ fontSize: 11 }}
              stroke={BRAND_COLORS.gray400}
              label={{
                value: 'Cost ($)',
                position: 'insideBottom',
                offset: -5,
                style: { fontSize: 11 },
              }}
            />
            <YAxis
              type="category"
              dataKey="email"
              tick={{ fontSize: 9 }}
              stroke={BRAND_COLORS.gray400}
              width={150}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
              }}
              formatter={(value: number) => `$${value.toFixed(4)}`}
              labelFormatter={(label: string) => `User: ${label}`}
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                      <p className="text-sm font-semibold text-gray-900 mb-1">{data.email}</p>
                      <p className="text-sm text-gray-700">
                        <span
                          className="font-mono font-bold"
                          style={{ color: BRAND_COLORS.danger }}
                        >
                          ${data.cost.toFixed(4)}
                        </span>
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{data.requests} requests</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Bar dataKey="cost" fill={BRAND_COLORS.danger} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Efficiency Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MiniMetricCard
          title="Cost per 1K Tokens"
          value={`$${efficiency_metrics.cost_per_1k_tokens.toFixed(4)}`}
          subtitle="Average efficiency"
          icon={<DollarSign className="w-4 h-4" />}
          color={BRAND_COLORS.primary}
        />
        <MiniMetricCard
          title="Tokens per Request"
          value={efficiency_metrics.avg_tokens_per_request.toFixed(0)}
          subtitle="Average complexity"
          icon={<Database className="w-4 h-4" />}
          color={BRAND_COLORS.purple}
        />
        <MiniMetricCard
          title="Success Rate"
          value={`${summary.success_rate.toFixed(1)}%`}
          subtitle={`${summary.total_requests} total requests`}
          icon={<CheckCircle className="w-4 h-4" />}
          color={BRAND_COLORS.success}
        />
      </div>
    </div>
  );
};

export default UnifiedAnalyticsDashboard;
